import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, RequestStatus, Priority, HttpMethod } from "../types";
import { getUnreadCount } from "../utils/notifications";
import apiService from "../../../service/Api";
import { isAdmin } from "../utils/permissions";

const STATUS_COLORS: Record<RequestStatus, "success" | "warning" | "info" | "error" | "default"> = {
  completed: "success",
  in_progress: "info",
  pending: "warning",
  blocked: "error",
  cancelled: "default",
};

const PRIORITY_COLORS: Record<Priority, "error" | "warning" | "info" | "default"> = {
  urgent: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: "#4caf50",
  POST: "#2196f3",
  PUT: "#ff9800",
  PATCH: "#9c27b0",
  DELETE: "#f44336",
};

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface AdminDashboardProps {
  requests: EndpointRequest[];
  onRequestUpdate?: (id: string, updates: Partial<EndpointRequest>) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  requests,
  // onRequestUpdate,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [activeTab, setActiveTab] = useState<"requested" | "completed">("requested");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openPointsDialog, setOpenPointsDialog] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string>(""); // Will store user_id as string
  const [pointsToAward, setPointsToAward] = useState<number>(0);
  const [pointsReason, setPointsReason] = useState<string>("");
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [stats, setStats] = useState({
    requested: 0,
    completed: 0,
    total: 0,
  });
  const [leaderboard, setLeaderboard] = useState<{
    date_awarded: string;
    leaderboard: Array<{
      user_id: number;
      name: string;
      requests_count: number;
      awarded_points: number;
    }>;
  } | null>(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  // Check if user is admin based on permissions
  const userIsAdmin = isAdmin(user);

  // Update notification count
  useEffect(() => {
    if (!userIsAdmin) return;
    const updateCount = () => {
      setNotificationCount(getUnreadCount("admin"));
    };
    updateCount();
    window.addEventListener("endpoint-request-notification-count-updated", updateCount);
    return () => {
      window.removeEventListener("endpoint-request-notification-count-updated", updateCount);
    };
  }, [userIsAdmin]);

  // Load stats from API
  const loadStats = React.useCallback(async () => {
    if (!userIsAdmin) return;
    
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        requested:
          (response.data.status_counts.pending || 0) +
          (response.data.status_counts.in_progress || 0),
        completed: response.data.status_counts.completed || 0,
        total: response.data.total_requests || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      // Fallback to calculating from requests
      setStats({
        requested: requests.filter((r) => r.status === "pending" || r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
        total: requests.length,
      });
    }
  }, [userIsAdmin, requests]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Reload stats when requests change
  useEffect(() => {
    if (userIsAdmin && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userIsAdmin, loadStats]);

  // Listen for notifications
  useEffect(() => {
    if (!userIsAdmin) return;

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      if (notification.type === "request_created") {
        toast.info(`New endpoint request: ${notification.component} - ${notification.task}`, {
          autoClose: 5000,
        });
      } else if (notification.type === "request_completed") {
        toast.success(`Endpoint completed: ${notification.component} - Endpoint: ${notification.endpoint}`, {
          autoClose: 6000,
        });
      }
    };

    window.addEventListener("endpoint-request-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("endpoint-request-notification", handleNotification as EventListener);
    };
  }, [userIsAdmin]);

  // Load leaderboard from API
  const loadLeaderboard = React.useCallback(async () => {
    if (!userIsAdmin) return;
    
    setLoadingLeaderboard(true);
    try {
      const response = await apiService.get<{
        date_awarded: string;
        leaderboard: Array<{
          user_id: number;
          name: string;
          requests_count: number;
          awarded_points: number;
        }>;
      }>("/api/endpoint-request/endpoint-requests/daily-leaderboard/");

      setLeaderboard(response.data);
    } catch (error: unknown) {
      console.error("Error loading leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [userIsAdmin]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  // Filter requests based on active tab
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (activeTab === "requested") {
      filtered = filtered.filter(
        (r) => r.status === "pending" || r.status === "in_progress"
      );
    } else if (activeTab === "completed") {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.createdBy?.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [requests, activeTab, statusFilter, searchTerm]);

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleAwardPoints = async () => {
    if (!selectedDeveloper || pointsToAward <= 0) {
      toast.error("Please select a developer and enter valid points");
      return;
    }

    // Validate max points (3)
    if (pointsToAward > 3) {
      toast.error("Cannot award more than 3 points");
      return;
    }

    try {
      // selectedDeveloper contains user_id as string
      const userId = parseInt(selectedDeveloper, 10);
      
      if (isNaN(userId)) {
        toast.error("Invalid developer selected");
        return;
      }

      // Find the developer from leaderboard to get name
      const developer = leaderboard?.leaderboard.find(
        (dev) => dev.user_id === userId
      );

      if (!developer) {
        toast.error("Developer not found in leaderboard");
        return;
      }

      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split("T")[0];

      // POST to API
      await apiService.post("/api/endpoint-request/endpoint-requests/developer-points/", {
        developer: userId,
        date_awarded: today,
        awarded_points: pointsToAward,
        reason_for_award: pointsReason || "Awarded by admin",
      });

      toast.success(
        `Awarded ${pointsToAward} points to ${developer.name}${pointsReason ? `: ${pointsReason}` : ""}`
      );

      // Reset dialog
      setOpenPointsDialog(false);
      setSelectedDeveloper("");
      setPointsToAward(0);
      setPointsReason("");

      // Reload leaderboard to show updated points
      await loadLeaderboard();

      // Dispatch event to update UI
      window.dispatchEvent(
        new CustomEvent("developer-points-updated", {
          detail: { username: developer.name, user_id: developer.user_id },
        })
      );
    } catch (error: unknown) {
      console.error("Error awarding points:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to award points. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
  };

  if (!userIsAdmin) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AdminIcon sx={{ fontSize: 40, color: primaryColor }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ color: "text.primary" }}>
                Admin Dashboard
              </Typography>
              {notificationCount > 0 && (
                <Chip
                  label={notificationCount}
                  color="error"
                  size="small"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Manage endpoint requests and award developer points
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<StarIcon />}
          onClick={() => setOpenPointsDialog(true)}
          sx={{
            backgroundColor: primaryColor,
            "&:hover": { backgroundColor: primaryColor, opacity: 0.9 },
            boxShadow: 2,
            px: 3,
            py: 1.5,
          }}
        >
          Award Points
        </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "warning.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Requested
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {stats.requested}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: "warning.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "success.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: primaryColor }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Requests
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color={primaryColor}>
                    {stats.total}
                  </Typography>
                </Box>
                <TrophyIcon sx={{ fontSize: 40, color: primaryColor, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Developer Leaderboard */}
      <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Developer Leaderboard
          </Typography>
          {leaderboard && (
            <Typography variant="body2" color="text.secondary">
              Date: {new Date(leaderboard.date_awarded).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        {loadingLeaderboard ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">Loading leaderboard...</Typography>
          </Box>
        ) : leaderboard && leaderboard.leaderboard.length > 0 ? (
          <Grid container spacing={2}>
            {leaderboard.leaderboard.slice(0, 5).map((dev, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={dev.user_id}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    backgroundColor: index === 0 ? "warning.light" : "background.paper",
                    border: index === 0 ? "2px solid" : "1px solid",
                    borderColor: index === 0 ? "warning.main" : "divider",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" noWrap>
                    {dev.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dev.requests_count} requests
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mt: 1 }}>
                    <StarIcon sx={{ fontSize: 16, color: "warning.main" }} />
                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                      {dev.awarded_points}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">No leaderboard data available</Typography>
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3, boxShadow: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 64,
            },
          }}
        >
          <Tab label={`Requested (${stats.requested})`} value="requested" />
          <Tab label={`Completed (${stats.completed})`} value="completed" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search by component, task, lead, or endpoint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as RequestStatus | "all")}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setStatusFilter("all");
                setSearchTerm("");
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ boxShadow: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Developer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow key={request.id || `request-${request.component}-${request.task}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.component}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={request.task}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {request.task}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.createdBy || request.lead || "Unknown"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {request.method && (
                          <Chip
                            label={request.method}
                            size="small"
                            sx={{
                              backgroundColor: METHOD_COLORS[request.method as HttpMethod] || "#666",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              height: 22,
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {request.endpoint || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status.replace("_", " ").toUpperCase()}
                        color={STATUS_COLORS[request.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority.toUpperCase()}
                        color={PRIORITY_COLORS[request.priority]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.875rem">
                        {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {request.imageUrl ? (
                        <Tooltip title="View Image">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              if (request.imageUrl) {
                                setPreviewImageUrl(request.imageUrl);
                                setImagePreviewOpen(true);
                              }
                            }}
                          >
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            // You can add a view dialog here if needed
                            toast.info(`Viewing request: ${request.component}`);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Award Points Dialog */}
      <Dialog open={openPointsDialog} onClose={() => setOpenPointsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StarIcon sx={{ color: "warning.main" }} />
            <Typography variant="h6">Award Points to Developer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {`Points will be added to the developer's daily leaderboard. Maximum 3 points can be awarded.`}
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Developer</InputLabel>
            <Select
              value={selectedDeveloper}
              label="Select Developer"
              onChange={(e) => setSelectedDeveloper(e.target.value)}
              disabled={!leaderboard || leaderboard.leaderboard.length === 0}
            >
              {leaderboard?.leaderboard.map((dev) => (
                <MenuItem key={dev.user_id} value={dev.user_id.toString()}>
                  {dev.name} ({dev.requests_count} requests, {dev.awarded_points} points)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Points to Award"
            type="number"
            value={pointsToAward}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              const clampedValue = !isNaN(value) && value >= 0 ? Math.min(value, 3) : 0;
              setPointsToAward(clampedValue);
            }}
            inputProps={{ min: 1, max: 3 }}
            sx={{ mb: 2 }}
            helperText="Enter the number of points to award (maximum 3 points)"
            error={pointsToAward > 3}
          />
          <TextField
            fullWidth
            label="Reason (Optional)"
            value={pointsReason}
            onChange={(e) => setPointsReason(e.target.value)}
            multiline
            rows={3}
            placeholder="e.g., Excellent work on completing multiple endpoint requests"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPointsDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAwardPoints}
            variant="contained"
            disabled={!selectedDeveloper || pointsToAward <= 0 || pointsToAward > 3}
            sx={{ backgroundColor: primaryColor }}
          >
            Award Points
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setPreviewImageUrl(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Endpoint Application Context</Typography>
            <IconButton
              onClick={() => {
                setImagePreviewOpen(false);
                setPreviewImageUrl(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Endpoint application context"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                toast.error("Failed to load image");
              }}
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

